import { cn } from "@/lib/utils";

interface DurationDisplayProps {
  durationMinutes: number | null | undefined;
  className?: string;
}

/**
 * DurationDisplay - formats journey duration in a human-readable format
 *
 * Examples:
 * - 135 minutes → "2h 15m"
 * - 1500 minutes (25 hours) → "1d 1h"
 * - null/undefined → null (renders nothing)
 */
export function DurationDisplay({
  durationMinutes,
  className,
}: DurationDisplayProps) {
  if (durationMinutes === null || durationMinutes === undefined) {
    return null;
  }

  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  let displayText: string;

  if (hours >= 24) {
    // Format as days and hours for durations 24+ hours
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    displayText = remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  } else if (hours > 0) {
    // Format as hours and minutes
    displayText = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  } else {
    // Less than an hour, show only minutes
    displayText = `${minutes}m`;
  }

  return (
    <span
      className={cn("text-sm text-muted-foreground", className)}
      aria-label={`Duration: ${displayText}`}
    >
      {displayText}
    </span>
  );
}
